import { BrandGuidelinesService } from '@inference.engine/guidelines-api-client';
import {
  GuidelineCreateDto,
  GuidelineListDto,
  GuidelineResponseDto,
  guidelinesHttp,
  GuidelineUpdateDto,
} from '@/lib/api/guidelines-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

/**
 * Fetches all guidelines for a given workspace
 * @param workspaceId - The workspace ID to fetch guidelines for
 * @returns Array of GuidelineResponseDto objects
 */
export const getGuidelinesByWorkspace = async (
  workspaceId: string,
): Promise<GuidelineResponseDto[]> => {
  const response = await BrandGuidelinesService.getGuidelinesByType({
    client: guidelinesHttp,
    query: {
      identifier: workspaceId,
      types: 'workspace',
    },
  });
  return response.data?.guidelines ?? [];
};

export type GetGuidelinesByFolderOptions = {
  suppressErrorNotification?: boolean;
};

/**
 * Fetches all guidelines for a given folder
 * @param folderId - The folder ID to fetch guidelines for
 * @param options - Optional per-request tuning (e.g. toast suppression)
 * @returns Array of GuidelineResponseDto objects
 */
export const getGuidelinesByFolder = async (
  folderId: string,
  options?: GetGuidelinesByFolderOptions,
): Promise<GuidelineResponseDto[]> => {
  const response = await BrandGuidelinesService.getGuidelinesByType({
    client: guidelinesHttp,
    query: {
      identifier: folderId,
      types: 'folder',
    },
    // When the resolution seam is active it owns the not-found UX, so a
    // cross-workspace 404 must not flash the global toast. Opt-in only:
    // every other caller keeps the pre-seam toast behavior.
    headers: options?.suppressErrorNotification
      ? // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
      : undefined,
  });
  return response.data?.guidelines ?? [];
};

/**
 * Creates a new guideline
 * @param data - The guideline data to create
 * @returns The created GuidelineResponseDto
 */
export const createGuideline = async (
  data: GuidelineCreateDto,
): Promise<GuidelineResponseDto | null> => {
  const response = await BrandGuidelinesService.createGuideline({
    client: guidelinesHttp,
    body: data,
  });
  return response.data ?? null;
};

/**
 * Updates an existing guideline
 * @param guidelineId - The guideline ID to update
 * @param data - The updated guideline data
 * @returns The updated GuidelineResponseDto
 */
export const updateGuideline = async (
  guidelineId: string,
  data: GuidelineUpdateDto,
): Promise<GuidelineResponseDto | null> => {
  const response = await BrandGuidelinesService.updateGuideline({
    client: guidelinesHttp,
    path: { guidelineId },
    body: data,
  });
  return response.data ?? null;
};

/**
 * Deletes a single guideline
 * @param guidelineId - The guideline ID to delete
 */
export const deleteGuideline = async (guidelineId: string): Promise<void> => {
  await BrandGuidelinesService.deleteGuideline({
    client: guidelinesHttp,
    path: { guidelineId },
  });
};

/**
 * Generates guidelines from uploaded files using AI
 * @param files - Array of files to analyze
 * @param workspaceId - The workspace ID for the generated guidelines
 * @param options - Optional project ID and folder ID for the generated guidelines
 * @returns GuidelineListDto with generated guidelines
 */
export const generateGuidelinesFromFiles = async (
  files: File[],
  workspaceId: string,
  options?: { projectId?: string; folderId?: string },
): Promise<GuidelineListDto | null> => {
  const response = await BrandGuidelinesService.generateGuidelines({
    client: guidelinesHttp,
    body: {
      files,
      workspaceId,
      projectId: options?.projectId,
      folderId: options?.folderId,
    },
  });
  return response.data ?? null;
};

/**
 * Imports guidelines from text/markdown files. Files in the structured format are parsed as
 * written; free prose is read by AI.
 * @param files - Array of text/markdown files to import
 * @param workspaceId - The workspace ID for the imported guidelines
 * @param options - Optional project ID and folder ID for the imported guidelines
 * @returns GuidelineListDto with imported guidelines
 */
export const importGuidelinesFromFiles = async (
  files: File[],
  workspaceId: string,
  options?: { projectId?: string; folderId?: string },
): Promise<GuidelineListDto | null> => {
  const response = await BrandGuidelinesService.importGuidelines({
    client: guidelinesHttp,
    body: {
      files,
      workspaceId,
      projectId: options?.projectId,
      folderId: options?.folderId,
    },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    throwOnError: true,
  });
  return response.data;
};
